import React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Plus, Calendar, MapPin, Search, Bell, Zap, Droplet, Ruler, Users, Image as ImageIcon, Map as MapIcon, Edit3, BarChart3, Inbox, Eye, Settings, Map, Camera, Truck, MousePointer2, Square, Trash2, Download, Music, Info, ShieldCheck, Tent, Globe, Star, ArrowRight, Instagram, Facebook, Linkedin, Youtube, Mail, Phone, CheckCircle, MessageSquare } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { doc, getDoc, updateDoc, deleteDoc, collection, query, where, onSnapshot } from 'firebase/firestore';
import { computeMatchScore } from '../utils/matchScore';
import { computeFestivalCompletion } from '../utils/profileCompletion';
import { DocumentDropzone } from '../components/DocumentDropzone';
import { ref as storageRef, deleteObject } from 'firebase/storage';
import { storage } from '../lib/firebase';
import { NotificationBell, createNotification } from '../components/NotificationBell';
import { UserMenu } from '../components/UserMenu';
import { Logo } from '../components/Logo';

interface PitchFormat {
  id: string;
  width: string;
  depth: string;
  flooring: string;
  price: string;
}

interface Festival {
  id: string;
  name: string;
  category: string;
  location: string;
  startDate: string;
  endDate: string;
  capacity?: number;
  dailyPax?: number;
  terrainType?: string;
  about?: string;
  accessInfo?: string;
  audienceProfile?: string;
  editionNumber?: string | number;
  pastAudience?: string;
  historyDescription?: string;
  ticketingUrl?: string;
  allowedSlots?: string[];
  musicStyles?: string[];
  totalStands?: number;
  customTimeRange?: string;
  technical?: {
    powerKva: number;
    has16A: boolean;
    has32A: boolean;
    has63A: boolean;
    hasDrinkingWater: boolean;
    wastewaterDetails: string;
    providesTables: boolean;
    nightSecurity?: string;
    pitches: PitchFormat[];
  };
  branding?: {
    description: string;
    coverImage: string;
    logoImage: string;
    gallery: string[];
    highlights?: string[];
    socials?: {
      instagram?: string;
      facebook?: string;
      tiktok?: string;
      linkedin?: string;
      website?: string;
      youtube?: string;
      twitter?: string;
    };
  };
  status?: string;
  planElements?: PlanElement[];
  planStands?: PlanElement[];
  standTypes?: string[];
  region?: string;
  insuranceUrl?: string;
}

interface PlanElement {
  id: string;
  type: 'stand' | 'zone' | 'pin_elec' | 'pin_eau';
  name?: string;
  x: number;
  y: number;
  w?: number;
  h?: number;
  price?: number;
}

export default function ManageFestivalDashboardPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [fest, setFest] = useState<Festival | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortByScore, setSortByScore] = useState(false);
  const [standsData, setStandsData] = useState<Record<string, any>>({});

  const [planElements, setPlanElements] = useState<PlanElement[]>([]);
  const [toolMode, setToolMode] = useState<'select' | 'zone' | 'pin_elec' | 'pin_eau'>('select');
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState({ x: 0, y: 0 });
  const [currentDraw, setCurrentDraw] = useState({ x: 0, y: 0, w: 0, h: 0 });
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  const [isDraggingElement, setIsDraggingElement] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const handleOptimizePlan = async () => {
    const approved = applications.filter(a => a.status === 'approved');
    let currentX = 20;
    let currentY = 20;
    let rowMaxH = 0;
    const padding = 20;
    
    const newPlacements: PlanElement[] = approved.map(app => {
      const match = app.format.match(/(\d+)x(\d+)/);
      const wM = match ? parseInt(match[1]) * 20 : 60; // 1m = 20px
      const hM = match ? parseInt(match[2]) * 20 : 60;
      
      if (currentX + wM > 780) {
        currentX = 20;
        currentY += rowMaxH + padding;
        rowMaxH = 0;
      }
      
      const p: PlanElement = { id: app.id, type: 'stand', name: app.standName, x: currentX, y: currentY, w: wM, h: hM };
      
      currentX += wM + padding;
      rowMaxH = Math.max(rowMaxH, hM);
      
      return p;
    });
    const updatedElements = [...planElements.filter(e => e.type !== 'stand'), ...newPlacements];
    setPlanElements(updatedElements);

    if (id) {
      try {
        const docRef = doc(db, 'festivals', id);
        await updateDoc(docRef, { planElements: updatedElements });
      } catch (err) {
        console.error("Failed to save plan optimization", err);
      }
    }
  };

  const savePlanElements = async (elements: PlanElement[]) => {
    if (!id) return;
    try {
      const docRef = doc(db, 'festivals', id);
      await updateDoc(docRef, { planElements: elements });
    } catch(err) {
      console.error(err);
    }
  };

  const handleMapMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (toolMode === 'select') return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    if (toolMode === 'zone') {
      setIsDrawing(true);
      setDrawStart({ x, y });
      setCurrentDraw({ x, y, w: 0, h: 0 });
    } else if (toolMode === 'pin_elec' || toolMode === 'pin_eau') {
      const newPin: PlanElement = {
        id: Date.now().toString(),
        type: toolMode,
        x: x - 15, // center pin roughly
        y: y - 15,
        w: 30,
        h: 30
      };
      setPlanElements(prev => {
        const newElements = [...prev, newPin];
        savePlanElements(newElements);
        return newElements;
      });
      // Return to select mode after placing pin
      setToolMode('select');
    }
  };

  const handleMapMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isDraggingElement && selectedElementId && toolMode === 'select') {
      setPlanElements(prev => prev.map(el => {
        if (el.id === selectedElementId) {
          return { ...el, x: e.clientX - dragOffset.x, y: e.clientY - dragOffset.y };
        }
        return el;
      }));
      return;
    }

    if (!isDrawing || toolMode !== 'zone') return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    setCurrentDraw({
      x: Math.min(drawStart.x, x),
      y: Math.min(drawStart.y, y),
      w: Math.abs(x - drawStart.x),
      h: Math.abs(y - drawStart.y)
    });
  };

  const handleMapMouseUp = () => {
    if (isDraggingElement) {
      setIsDraggingElement(false);
      savePlanElements(planElements);
      return;
    }

    if (isDrawing && toolMode === 'zone') {
      if (currentDraw.w > 20 && currentDraw.h > 20) {
        const newZone: PlanElement = {
          id: Date.now().toString(),
          type: 'zone',
          name: 'Nouvelle Zone',
          price: 150,
          x: currentDraw.x,
          y: currentDraw.y,
          w: currentDraw.w,
          h: currentDraw.h
        };
        setPlanElements(prev => {
          const newElements = [...prev, newZone];
          savePlanElements(newElements);
          return newElements;
        });
        setSelectedElementId(newZone.id);
      }
      setToolMode('select');
    }
    setIsDrawing(false);
  };

  const handleDeleteElement = () => {
    if (!selectedElementId) return;
    const newElements = planElements.filter(e => e.id !== selectedElementId);
    setPlanElements(newElements);
    setSelectedElementId(null);
    savePlanElements(newElements);
  };

  const handleUpdatePrice = (priceStr: string) => {
    if (!selectedElementId) return;
    const price = parseInt(priceStr) || 0;
    const newElements = planElements.map(e => e.id === selectedElementId ? { ...e, price } : e);
    setPlanElements(newElements);
    savePlanElements(newElements);
  };

  const [mockApplications] = useState<any[]>([]);
  const [applications, setApplications] = useState<any[]>(mockApplications);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [editDescriptionText, setEditDescriptionText] = useState('');
  const [isSavingDescription, setIsSavingDescription] = useState(false);

  const [isEditingEvent, setIsEditingEvent] = useState(false);
  const [editedFest, setEditedFest] = useState<Partial<Festival>>({});
  const [isSavingEvent, setIsSavingEvent] = useState(false);

  const [expandedAppId, setExpandedAppId] = useState<string | null>(null);
  const [contractStates, setContractStates] = useState<Record<string, { status: 'idle' | 'generating' | 'ready' | 'error', url?: string, error?: string }>>({});

  const [isExporting, setIsExporting] = useState(false);

  const MUSIC_STYLES = [
    { id: 'electro', label: 'Électronique' },
    { id: 'rock', label: 'Rock / Métal' },
    { id: 'pop', label: 'Pop' },
    { id: 'rap', label: 'Rap / Hip-Hop' },
    { id: 'jazz', label: 'Jazz / Blues' },
    { id: 'reggae', label: 'Reggae' },
    { id: 'world', label: 'Musiques du Monde' },
    { id: 'mixed', label: 'Mixte / Éclectique' }
  ];

  const TIME_SLOTS = [
    { id: 'day', label: 'Journée', description: 'Ouverture midi - 20h' },
    { id: 'night', label: 'Nuit', description: 'Ouverture 20h - matin' },
    { id: 'full_pass', label: 'Full Pass', description: 'Toute la durée' },
    { id: 'custom', label: 'Plage libre', description: 'Horaires sur mesure' }
  ];

  // Envoi d'email d'acceptation/refus via le backend (fire-and-forget, non bloquant).
  const sendApplicationEmail = async (applicationId: string, decision: 'accepted' | 'rejected') => {
    try {
      const idToken = await user?.getIdToken();
      await fetch(`${import.meta.env.VITE_API_URL || ''}/api/send-application-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({ applicationId, decision }),
      });
    } catch (e) {
      console.error('Envoi email candidature échoué', e);
    }
  };

  const handleExportLogistics = async () => {
    if (!id) return;
    setIsExporting(true);
    try {
      const idToken = await user?.getIdToken();
      const res = await fetch('/api/export-logistics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({ festivalId: id })
      });
      const data = await res.json();
      if(data.url) {
        window.open(data.url, '_blank');
      } else {
        throw new Error(data.error || 'Erreur inconnue');
      }
    } catch(err: any) {
      alert(`Erreur d'export: ${err.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  const handleGenerateContract = async (app: any) => {
    setContractStates(prev => ({...prev, [app.id]: { status: 'generating' }}));
    try {
      const idToken = await user?.getIdToken();
      const res = await fetch('/api/generate-contract', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({
          festivalId: id,
          standId: app.id,
          standName: app.standName,
          applicantName: app.applicantName,
          format: app.format,
          price: 450 // Default mock price
        })
      });
      const data = await res.json();
      if(data.url) {
        setContractStates(prev => ({...prev, [app.id]: { status: 'ready', url: data.url }}));
      } else {
        throw new Error(data.error || 'Erreur inconnue');
      }
    } catch(err: any) {
      setContractStates(prev => ({...prev, [app.id]: { status: 'error', error: err.message }}));
    }
  };

  const handleSaveEvent = async () => {
    if (!id || !editedFest) return;
    setIsSavingEvent(true);
    try {
      const docRef = doc(db, 'festivals', id);
      const updates = { ...editedFest };
      delete updates.id;
      
      await updateDoc(docRef, updates);
      
      setFest(prev => prev ? { ...prev, ...updates } : null);
      setIsEditingEvent(false);
    } catch (err) {
      console.error("Erreur lors de la sauvegarde :", err);
      alert("Une erreur est survenue lors de la sauvegarde.");
    } finally {
      setIsSavingEvent(false);
    }
  };

  const handleDeleteFestival = async () => {
    if (!fest || deleteConfirm !== fest.name) return;
    setIsDeleting(true);
    try {
      if (id) {
        await deleteDoc(doc(db, 'festivals', id));
        navigate('/my-festivals');
      }
    } catch (err) {
      console.error("Error deleting festival:", err);
      alert("Une erreur est survenue lors de la suppression.");
      setIsDeleting(false);
    }
  };

  const filteredApplications = applications
    .filter(app => {
      const matchesSearch = app.standName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            app.applicantName.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      if (!sortByScore) return 0;
      const scoreA = standsData[a.standId] ? computeMatchScore(standsData[a.standId], fest || {}).total : 0;
      const scoreB = standsData[b.standId] ? computeMatchScore(standsData[b.standId], fest || {}).total : 0;
      return scoreB - scoreA;
    });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentApplications = filteredApplications.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);

  const handleUpdateStatus = async (appId: string, newStatus: string) => {
    const app = applications.find(a => a.id === appId);
    if (!app) return;

    // 1. Update in UI
    setApplications(apps => apps.map(app => app.id === appId ? { ...app, status: newStatus } : app));

    // La session Stripe n'est plus créée ici : le stand la génère lui-même au
    // clic « Payer » (Embedded Checkout) — évite les liens de session expirés.

    // 2. Notify stand owner
    let notificationTitle = '';
    let notificationMessage = '';
    let notificationType: 'success' | 'warning' | 'info' = 'info';

    if (newStatus === 'approuve_paiement_requis') {
      notificationTitle = "Candidature retenue ! 🥂";
      notificationMessage = `Félicitations ! Votre stand pour "${fest.name}" est retenu. Réglez vos frais de gestion Festin pour confirmer votre place.`;
      notificationType = 'success';
      
      // Email d'acceptation envoyé via le backend (Admin SDK).
      sendApplicationEmail(appId, 'accepted');
    } else if (newStatus === 'approved' || newStatus === 'confirme') {
      notificationTitle = "Stand confirmé ! 🎉";
      notificationMessage = `Votre emplacement pour "${fest.name}" est maintenant officiellement confirmé.`;
      notificationType = 'success';
    } else if (newStatus === 'refuse') {
      notificationTitle = "Candidature refusée 😔";
      notificationMessage = `Désolé, votre candidature pour "${fest.name}" n'a pas été retenue.`;
      notificationType = 'warning';

      // Email de refus envoyé via le backend (Admin SDK).
      sendApplicationEmail(appId, 'rejected');
    } else {
      notificationTitle = "Statut mis à jour";
      notificationMessage = `Le statut de votre candidature pour "${fest.name}" a changé : ${newStatus}`;
      notificationType = 'info';
    }

    await createNotification({
      userId: app.applicantId,
      title: notificationTitle,
      message: notificationMessage,
      type: notificationType,
      link: '/stand/festivals'
    });

    // 3. Update in Firebase if it's a real application
    if (appId.startsWith('app')) return; // Mock application

    try {
      const docRef = doc(db, 'applications', appId);
      await updateDoc(docRef, { status: newStatus });
    } catch (err) {
      console.error("Failed to update status", err);
      alert("Erreur lors de la mise à jour du statut dans la base de données.");
    }
  };

  useEffect(() => {
    const fetchFestival = async () => {
      if (!id) return;
      try {
        const docRef = doc(db, 'festivals', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setFest({ id: docSnap.id, ...data } as Festival);
          if (data.planElements) {
            setPlanElements(data.planElements);
          } else if (data.planStands) {
            setPlanElements(data.planStands);
          }
        }
      } catch (err) {
        console.error("Failed to fetch festival", err);
      } finally {
        setLoading(false);
      }
    };
    fetchFestival();
  }, [id]);

  // Fetch applications + stand data for scoring
  useEffect(() => {
    if (!id) return;
    const q = query(collection(db, 'applications'), where('festivalId', '==', id));
    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const dbApps = snapshot.docs.map(d => ({ id: d.id, ...d.data() })) as any[];
      setApplications([...mockApplications, ...dbApps]);

      // Batch-fetch stand data for score computation
      const uniqueStandIds = [...new Set(dbApps.map((a: any) => a.standId).filter(Boolean))];
      const results: Record<string, any> = {};
      await Promise.all(
        uniqueStandIds.map(async (standId) => {
          try {
            const snap = await getDoc(doc(db, 'stands', standId));
            if (snap.exists()) results[standId] = snap.data();
          } catch {}
        })
      );
      setStandsData(results);
    }, (error) => {
      console.error("Error fetching applications:", error);
    });

    return () => unsubscribe();
  }, [id]);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>, type: 'logoImage' | 'coverImage') => {
    const file = event.target.files?.[0];
    if (!file || !id) return;

    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      alert('Veuillez uploader uniquement des formats PNG ou JPG.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = async () => {
        const canvas = document.createElement('canvas');
        let MAX_WIDTH = type === 'coverImage' ? 1200 : 400;
        let MAX_HEIGHT = type === 'coverImage' ? 800 : 400;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width;
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height;
            height = MAX_HEIGHT;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height);
        }
        
        const dataUrl = canvas.toDataURL('image/jpeg', 0.6);

        try {
          const docRef = doc(db, 'festivals', id);
          await updateDoc(docRef, {
            [`branding.${type}`]: dataUrl
          });
          setFest(prev => prev ? {
            ...prev, 
            branding: { ...prev.branding, [type]: dataUrl } 
          } : null);
        } catch (err) {
          console.error("Erreur lors de la mise à jour de l'image", err);
          alert("L'image est toujours trop volumineuse après compression. Veuillez choisir une image plus petite.");
        }
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  const handleSaveName = async () => {
    if (!id || !newName) return;
    try {
      const docRef = doc(db, 'festivals', id);
      await updateDoc(docRef, { name: newName });
      setFest(prev => prev ? { ...prev, name: newName } : null);
      setIsEditingName(false);
    } catch (err) {
      console.error("Error saving name:", err);
      alert("Erreur lors de la sauvegarde du nom.");
    }
  };

  const handleSaveDescription = async () => {
    if (!id) return;
    setIsSavingDescription(true);
    try {
      const docRef = doc(db, 'festivals', id);
      await updateDoc(docRef, {
        'branding.description': editDescriptionText
      });
      setFest(prev => prev ? {
        ...prev,
        branding: { ...prev.branding, description: editDescriptionText }
      } : null);
      setIsEditingDescription(false);
    } catch (err) {
      console.error("Erreur saving description", err);
      alert("Une erreur est survenue lors de la sauvegarde.");
    } finally {
      setIsSavingDescription(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex justify-center items-center">
        <div className="w-8 h-8 border-4 border-[#FF5C00]/20 border-t-[#FF5C00] rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!fest) {
    return <div className="min-h-screen bg-black text-white flex justify-center items-center">Événement introuvable</div>;
  }

  const standTypeLabels: Record<string, string> = {
    'food': 'Foodtrucks',
    'vinyles': 'Vinyles',
    'craft': 'Artisanat',
    'fashion': 'Mode',
    'tattoo': 'Tatouage',
    'associative': 'Associatif',
    'partners': 'Partenaires',
    'service': 'Service',
    'other': 'Autre'
  };

  return (
    <div className="min-h-screen bg-black text-white font-sans">
      
      {/* Sub-menu Navigation */}
      <div className="sticky top-16 z-40 bg-black/80 backdrop-blur-[10px] border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 flex items-center space-x-8 text-sm font-bold text-gray-400 overflow-x-auto no-scrollbar">
           <button 
             onClick={() => setActiveTab('dashboard')} 
             className={`py-3 whitespace-nowrap flex items-center transition-colors border-b-2 ${activeTab === 'dashboard' ? 'text-[#FF5C00] border-[#FF5C00]' : 'border-transparent hover:text-white hover:border-white/30'}`}
           >
             <BarChart3 className="w-4 h-4 mr-2"/> Dashboard
           </button>
           <button 
             onClick={() => setActiveTab('applications')}
             className={`py-3 whitespace-nowrap flex items-center transition-colors border-b-2 ${activeTab === 'applications' ? 'text-[#FF5C00] border-[#FF5C00]' : 'border-transparent hover:text-white hover:border-white/30'}`}
           >
             <Inbox className="w-4 h-4 mr-2"/> 
             Candidatures
             {applications.length > 0 && (
               <span className={`ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-black ${activeTab === 'applications' ? 'bg-[#FF5C00] text-white shadow-[0_0_10px_rgba(255,92,0,0.5)]' : 'bg-white/10 text-gray-400'}`}>
                 {applications.length}
               </span>
             )}
           </button>
           <button 
             onClick={() => navigate(`/festival/${fest.id}`)}
             className="hover:text-white border-b-2 border-transparent hover:border-white/30 py-3 whitespace-nowrap flex items-center transition-colors"
           >
             <Eye className="w-4 h-4 mr-2"/> Ma Vitrine
           </button>
           {/* Desktop Version */}
           <button 
             onClick={() => setActiveTab('plan')}
             className={`hidden md:flex py-3 whitespace-nowrap items-center transition-colors border-b-2 ${activeTab === 'plan' ? 'text-[#FF5C00] border-[#FF5C00]' : 'border-transparent hover:text-white hover:border-white/30'}`}
           >
             <Map className="w-4 h-4 mr-2"/> Plan & Technique
           </button>

           {/* Mobile Version (Muted) */}
           <button 
             onClick={() => alert("Le module Plan & Technique est disponible uniquement sur ordinateur ou tablette pour une meilleure expérience d'édition 🖥️")}
             className="md:hidden flex py-3 whitespace-nowrap items-center text-gray-500/50 border-b-2 border-transparent cursor-default"
           >
             <Map className="w-4 h-4 mr-2 opacity-50"/> 
             <span className="opacity-50">Plan & Tech.</span>
             <span className="ml-1 text-[9px] italic">(PC uniquement 🖥️)</span>
           </button>
           <button 
             onClick={() => setActiveTab('settings')}
             className={`py-3 whitespace-nowrap flex items-center transition-colors border-b-2 ${activeTab === 'settings' ? 'text-[#FF5C00] border-[#FF5C00]' : 'border-transparent hover:text-white hover:border-white/30'}`}
           >
             <Settings className="w-4 h-4 mr-2"/> Paramètres
           </button>
        </div>
      </div>
    
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-8">
        
        {activeTab === 'dashboard' && (
        <div className="bg-[#121212] border border-white/10 rounded-[24px] md:rounded-3xl overflow-hidden shadow-xl group">
          {/* Branding Hero */}
          <div className="relative h-64 md:h-80 w-full overflow-hidden group/cover">
            <img 
              src={fest.branding?.coverImage || "https://images.unsplash.com/photo-1540039155733-d7696d4ebc59?auto=format&fit=crop&q=80"} 
              alt={fest.name} 
              className="w-full h-full object-cover group-hover/cover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-[#121212]/60 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#121212]/90 to-transparent" />
            
            <label className="absolute top-6 left-6 cursor-pointer bg-black/50 hover:bg-[#FF5C00] backdrop-blur-md px-4 py-2 rounded-xl text-white font-bold text-sm flex items-center transition-all border border-white/10 hover:border-[#FF5C00] opacity-0 group-hover/cover:opacity-100 z-20">
              <Camera className="w-4 h-4 mr-2" /> Changer l'image
              <input 
                type="file" 
                accept="image/png, image/jpeg" 
                className="hidden" 
                onChange={(e) => handleImageUpload(e, 'coverImage')}
                required
              />
            </label>

            <div className="absolute bottom-6 left-6 md:left-10 flex items-end">
              <div className="w-24 h-24 bg-[#1A1A1A] rounded-2xl border border-white/10 overflow-hidden mr-6 shadow-2xl relative z-20 flex items-center justify-center shrink-0 group/logo">
                {fest.branding?.logoImage ? (
                   <img src={fest.branding.logoImage} alt="Logo" className="w-full h-full object-cover group-hover/logo:opacity-50 transition-opacity" />
                ) : (
                   <span className="text-xs font-black text-[#FF5C00] group-hover/logo:opacity-50 transition-opacity">LOGO</span>
                )}
                <label className="absolute inset-0 flex flex-col items-center justify-center cursor-pointer bg-black/60 opacity-0 group-hover/logo:opacity-100 transition-opacity">
                  <Camera className="w-6 h-6 text-white mb-1" />
                  <span className="text-[10px] text-white font-bold uppercase">Modifier</span>
                  <input 
                    type="file" 
                    accept="image/png, image/jpeg" 
                    className="hidden" 
                    onChange={(e) => handleImageUpload(e, 'logoImage')}
                    required
                  />
                </label>
              </div>
              <div className="pb-1">
                <div className="flex items-center space-x-3 mb-2">
                  <span className="px-3 py-1 bg-[#4169E1]/20 text-[#4169E1] rounded-lg text-xs font-bold uppercase tracking-wider backdrop-blur-md border border-[#4169E1]/30">
                    {fest.category || 'Catégorie'}
                  </span>
                  <span className={`px-3 py-1 rounded-lg text-xs font-bold uppercase tracking-wider backdrop-blur-md border ${fest.status === 'published' ? 'bg-green-500/20 text-green-500 border-green-500/30' : 'bg-orange-500/20 text-orange-500 border-orange-500/30'}`}>
                    {fest.status === 'published' ? 'Publié' : 'Brouillon'}
                  </span>
                </div>
                {isEditingName ? (
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      className="text-3xl md:text-4xl font-black text-white bg-black/50 border border-white/20 rounded-xl px-4 py-2 w-full focus:outline-none focus:border-[#FF5C00]"
                    />
                    <button 
                      onClick={handleSaveName}
                      className="bg-[#FF5C00] text-white px-4 py-2 rounded-xl font-bold"
                    >
                      OK
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-3">
                    <h2 className="text-2xl md:text-4xl font-black text-white shadow-black drop-shadow-2xl tracking-tight leading-none">{fest.name}</h2>
                    <button 
                      onClick={() => { setNewName(fest.name); setIsEditingName(true); }} 
                      className="p-2 text-gray-500 hover:text-white"
                    >
                      <Edit3 className="w-5 h-5" />
                    </button>
                    <button 
                      onClick={() => {
                        sessionStorage.setItem('activeFestivalId', fest.id);
                        navigate('/choose-creation-method');
                      }} 
                      className="hidden md:block text-xs font-black uppercase tracking-widest text-[#FF5C00] hover:text-white border border-[#FF5C00] hover:border-white px-4 py-2 rounded-xl transition-all h-11"
                    >
                      Édition complète
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            <div className="absolute top-4 md:top-6 right-4 md:right-6 flex flex-col md:flex-row items-end md:items-center space-y-2 md:space-y-0 md:space-x-3">
              <button 
                onClick={() => {
                  sessionStorage.setItem('activeFestivalId', fest.id);
                  navigate('/create-festival');
                }} 
                className="md:hidden bg-[#FF5C00] text-white px-4 h-11 rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center shadow-lg shadow-[#FF5C00]/20 active:scale-95 transition-all"
              >
                <Zap className="w-4 h-4 mr-2" /> Édition complète
              </button>
              
              {isEditingEvent ? (
                <div className="flex items-center space-x-2">
                  <button 
                    onClick={() => setIsEditingEvent(false)}
                    className="bg-black/50 hover:bg-white/10 backdrop-blur-md px-4 h-11 rounded-xl text-white font-bold text-sm flex items-center transition-all border border-white/10"
                  >
                    Annuler
                  </button>
                  <button 
                    onClick={handleSaveEvent}
                    disabled={isSavingEvent}
                    className="bg-[#FF5C00] hover:bg-[#FF5C00]/80 backdrop-blur-md px-4 h-11 rounded-xl text-white font-bold text-sm flex items-center transition-all disabled:opacity-50"
                  >
                    {isSavingEvent ? '...' : 'OK'}
                  </button>
                </div>
              ) : (
                <button 
                  onClick={() => {
                    setEditedFest(fest);
                    setIsEditingEvent(true);
                  }}
                  className="bg-black/50 hover:bg-[#FF5C00] backdrop-blur-md px-4 h-11 rounded-xl text-white font-bold text-sm flex items-center transition-all border border-white/10 hover:border-[#FF5C00]"
                >
                  <Edit3 className="w-4 h-4 mr-2" /> <span className="hidden sm:inline">Éditer l'événement</span><span className="sm:hidden">Éditer</span>
                </button>
              )}
            </div>
          </div>

          <div className="p-6 md:p-10">
            {/* Completion bar */}
            {(() => {
              const { criticalPct, recommendedPct, overallPct, criticalFilled, criticalTotal, recommendedFilled, recommendedTotal } = computeFestivalCompletion(fest as Record<string, any>);
              const color = overallPct >= 80 ? 'text-green-400' : overallPct >= 50 ? 'text-[#FF5C00]' : 'text-red-400';
              return (
                <div className="mb-8 bg-white/5 border border-white/10 rounded-2xl p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-4">
                      <span className="text-sm font-bold text-white">Complétion du profil festival</span>
                      <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded-full bg-[#FF5C00]" />
                        <span className="text-gray-500 text-xs font-bold">Essentiel <span className="text-white">{criticalFilled}/{criticalTotal}</span></span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded-full bg-blue-400" />
                        <span className="text-gray-500 text-xs font-bold">Recommandé <span className="text-white">{recommendedFilled}/{recommendedTotal}</span></span>
                      </div>
                    </div>
                    <span className={`text-sm font-black tabular-nums ${color}`}>{overallPct}%</span>
                  </div>
                  <div className="h-1.5 bg-white/5 rounded-full overflow-hidden flex">
                    <div className="h-full bg-[#FF5C00] transition-all duration-700 ease-out" style={{ width: `${criticalPct * 0.7}%` }} />
                    <div className="h-full bg-blue-400/60 transition-all duration-700 ease-out" style={{ width: `${recommendedPct * 0.3}%` }} />
                  </div>
                  {overallPct < 100 && (
                    <button
                      onClick={() => { sessionStorage.setItem('activeFestivalId', fest.id); navigate('/create-festival'); }}
                      className="mt-3 text-xs text-[#FF5C00] hover:text-white font-bold transition-colors"
                    >
                      Compléter le profil →
                    </button>
                  )}
                </div>
              );
            })()}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              
              {/* Left Col: Basics & Pitch */}
              <div className="lg:col-span-2 space-y-8">
                 <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                   <div className="bg-[#1A1A1A] p-4 rounded-xl border border-white/5">
                     <Calendar className="w-6 h-6 text-[#FF5C00] mb-2" />
                     <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">Dates</p>
                     {isEditingEvent ? (
                       <div className="flex flex-col space-y-2 mt-2">
                         <input type="date" value={editedFest.startDate || ''} onChange={e => setEditedFest({...editedFest, startDate: e.target.value})} className="bg-black/50 border border-white/10 rounded px-2 py-1 text-xs text-white" />
                         <input type="date" value={editedFest.endDate || ''} onChange={e => setEditedFest({...editedFest, endDate: e.target.value})} className="bg-black/50 border border-white/10 rounded px-2 py-1 text-xs text-white" />
                       </div>
                     ) : (
                       <p className="text-sm font-semibold truncate">{fest.startDate || '-'} au {fest.endDate || '-'}</p>
                     )}
                   </div>
                   <div className="bg-[#1A1A1A] p-4 rounded-xl border border-white/5">
                     <MapPin className="w-6 h-6 text-[#4169E1] mb-2" />
                     <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">Localisation</p>
                     {isEditingEvent ? (
                       <input type="text" value={editedFest.location || ''} onChange={e => setEditedFest({...editedFest, location: e.target.value})} className="mt-2 w-full bg-black/50 border border-white/10 rounded px-2 py-1 text-sm text-white" placeholder="Ville..." />
                     ) : (
                       <p className="text-sm font-semibold truncate">{fest.location || '-'}</p>
                     )}
                   </div>
                   <div className="bg-[#1A1A1A] p-4 rounded-xl border border-white/5">
                     <Users className="w-6 h-6 text-purple-500 mb-2" />
                     <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">Jauge max / J</p>
                     {isEditingEvent ? (
                       <input type="number" value={editedFest.dailyPax || ''} onChange={e => setEditedFest({...editedFest, dailyPax: parseInt(e.target.value) || 0})} className="mt-2 w-full bg-black/50 border border-white/10 rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-[#FF5C00]" />
                     ) : (
                       <p className="text-sm font-semibold">{fest.dailyPax || '-'}</p>
                     )}
                   </div>
                   <div className="bg-[#1A1A1A] p-4 rounded-xl border border-white/5">
                     <Tent className="w-6 h-6 text-[#FF5C00] mb-2" />
                     <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">Total Stands</p>
                     {isEditingEvent ? (
                       <input type="number" value={editedFest.totalStands || 0} onChange={e => setEditedFest({...editedFest, totalStands: parseInt(e.target.value) || 0})} className="mt-2 w-full bg-black/50 border border-white/10 rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-[#FF5C00]" />
                     ) : (
                       <p className="text-sm font-semibold">{fest.totalStands || 0}</p>
                     )}
                   </div>
                   <div className="bg-[#1A1A1A] p-4 rounded-xl border border-white/5">
                     <MapIcon className="w-6 h-6 text-green-500 mb-2" />
                     <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">Type de terrain</p>
                     {isEditingEvent ? (
                       <select value={editedFest.terrainType || ''} onChange={e => setEditedFest({...editedFest, terrainType: e.target.value})} className="mt-2 w-full bg-black/50 border border-white/10 rounded px-2 py-1 text-sm text-white">
                         <option value="grass">Herbe</option>
                         <option value="asphalt">Bitume</option>
                         <option value="sand">Sable</option>
                         <option value="mixed">Mixte</option>
                       </select>
                     ) : (
                       <p className="text-sm font-semibold">
                         {fest.terrainType === 'grass' ? 'Herbe' : 
                          fest.terrainType === 'asphalt' ? 'Bitume' : 
                          fest.terrainType === 'sand' ? 'Sable' : 
                          fest.terrainType === 'mixed' ? 'Mixte' : fest.terrainType || '-'}
                       </p>
                     )}
                   </div>
                 </div>

                 {fest.category === 'music' && (
                    <div className="bg-[#1A1A1A] p-6 rounded-2xl border border-white/5">
                      <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4 flex items-center">
                        <Music className="w-4 h-4 mr-2 text-purple-500" /> Styles Musicaux
                      </h3>
                      {isEditingEvent ? (
                        <div className="flex flex-wrap gap-2">
                          {MUSIC_STYLES.map(style => (
                            <button
                              key={style.id}
                              onClick={() => {
                                const current = editedFest.musicStyles || [];
                                const next = current.includes(style.id)
                                  ? current.filter(id => id !== style.id)
                                  : [...current, style.id];
                                setEditedFest({ ...editedFest, musicStyles: next });
                              }}
                              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${
                                editedFest.musicStyles?.includes(style.id)
                                  ? 'bg-purple-500/20 text-purple-500 border-purple-500/50 shadow-[0_0_10px_rgba(168,85,247,0.3)]'
                                  : 'bg-black/40 text-gray-500 border-white/5 hover:border-white/20'
                              }`}
                            >
                              {style.label}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {fest.musicStyles && fest.musicStyles.length > 0 ? (
                            fest.musicStyles.map(id => (
                              <span key={id} className="px-2.5 py-1 bg-purple-500/10 text-purple-500 border border-purple-500/20 rounded-md text-xs font-bold">
                                {MUSIC_STYLES.find(s => s.id === id)?.label || id}
                              </span>
                            ))
                          ) : (
                            <span className="text-gray-500 italic text-sm">Non spécifié</span>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                    <div className="bg-[#1A1A1A] p-6 rounded-2xl border border-white/5">
                      <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4 flex items-center">
                        <Zap className="w-4 h-4 mr-2 text-[#FF5C00]" /> Créneaux Horaires Autorisés
                      </h3>
                      {isEditingEvent ? (
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            {TIME_SLOTS.map(slot => (
                              <button
                                key={slot.id}
                                onClick={() => {
                                  const current = editedFest.allowedSlots || [];
                                  const next = current.includes(slot.id)
                                    ? current.filter(id => id !== slot.id)
                                    : [...current, slot.id];
                                  setEditedFest({ ...editedFest, allowedSlots: next });
                                }}
                                className={`p-3 rounded-xl border text-left transition-all ${
                                  editedFest.allowedSlots?.includes(slot.id)
                                    ? 'bg-[#FF5C00]/10 border-[#FF5C00] shadow-[0_0_15px_rgba(255,92,0,0.2)]'
                                    : 'bg-black/40 border-white/10 text-gray-500 hover:border-white/30'
                                }`}
                              >
                                <div className={`text-xs font-black uppercase mb-1 ${editedFest.allowedSlots?.includes(slot.id) ? 'text-white' : 'text-gray-500'}`}>
                                  {slot.label}
                                </div>
                                <div className="text-[10px] opacity-60 leading-tight">{slot.description}</div>
                              </button>
                            ))}
                          </div>
                          
                          {editedFest.allowedSlots?.includes('custom') && (
                            <div className="animate-in fade-in slide-in-from-top-2">
                              <label className="block text-[10px] font-black text-[#FF5C00] mb-2 uppercase tracking-widest">Précisez vos horaires (ex: 14h - 02h)</label>
                              <input 
                                type="text"
                                value={editedFest.customTimeRange || ''}
                                onChange={e => setEditedFest({...editedFest, customTimeRange: e.target.value})}
                                placeholder="Ex: 14h00 - 02h00"
                                className="w-full bg-black/40 border border-[#FF5C00]/30 rounded-xl px-4 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#FF5C00]"
                              />
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {fest.allowedSlots && fest.allowedSlots.length > 0 ? (
                            fest.allowedSlots.map(id => (
                              <span key={id} className="px-2.5 py-1 bg-[#FF5C00]/10 text-[#FF5C00] border border-[#FF5C00]/20 rounded-md text-xs font-bold">
                                {id === 'custom' ? (fest.customTimeRange || 'Plage libre') : (TIME_SLOTS.find(s => s.id === id)?.label || id)}
                              </span>
                            ))
                          ) : (
                            <span className="text-gray-500 italic text-sm">Non spécifié</span>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="bg-[#1A1A1A] p-6 rounded-2xl border border-white/5">
                      <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4 flex items-center">
                        <Globe className="w-4 h-4 mr-2 text-blue-500" /> Réseaux Sociaux
                      </h3>
                      {isEditingEvent ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-3">
                            <div className="relative">
                              <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-pink-500" />
                              <input 
                                type="text"
                                value={editedFest.branding?.socials?.instagram || ''}
                                onChange={e => setEditedFest({...editedFest, branding: {...editedFest.branding!, socials: {...editedFest.branding?.socials, instagram: e.target.value}}})}
                                placeholder="Lien Instagram"
                                className="w-full bg-black/40 border border-white/10 rounded-xl pl-10 pr-4 py-2 text-xs text-white focus:outline-none focus:border-pink-500/50"
                              />
                            </div>
                            <div className="relative">
                              <Facebook className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-600" />
                              <input 
                                type="text"
                                value={editedFest.branding?.socials?.facebook || ''}
                                onChange={e => setEditedFest({...editedFest, branding: {...editedFest.branding!, socials: {...editedFest.branding?.socials, facebook: e.target.value}}})}
                                placeholder="Lien Facebook"
                                className="w-full bg-black/40 border border-white/10 rounded-xl pl-10 pr-4 py-2 text-xs text-white focus:outline-none focus:border-blue-600/50"
                              />
                            </div>
                            <div className="relative">
                              <Music className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white" />
                              <input 
                                type="text"
                                value={editedFest.branding?.socials?.tiktok || ''}
                                onChange={e => setEditedFest({...editedFest, branding: {...editedFest.branding!, socials: {...editedFest.branding?.socials, tiktok: e.target.value}}})}
                                placeholder="Lien TikTok"
                                className="w-full bg-black/40 border border-white/10 rounded-xl pl-10 pr-4 py-2 text-xs text-white focus:outline-none focus:border-white/20"
                              />
                            </div>
                          </div>
                          <div className="space-y-3">
                            <div className="relative">
                              <Linkedin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-500" />
                              <input 
                                type="text"
                                value={editedFest.branding?.socials?.linkedin || ''}
                                onChange={e => setEditedFest({...editedFest, branding: {...editedFest.branding!, socials: {...editedFest.branding?.socials, linkedin: e.target.value}}})}
                                placeholder="Lien LinkedIn"
                                className="w-full bg-black/40 border border-white/10 rounded-xl pl-10 pr-4 py-2 text-xs text-white focus:outline-none focus:border-blue-500/50"
                              />
                            </div>
                            <div className="relative">
                              <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-600" />
                              <input 
                                type="text"
                                value={editedFest.branding?.socials?.youtube || ''}
                                onChange={e => setEditedFest({...editedFest, branding: {...editedFest.branding!, socials: {...editedFest.branding?.socials, youtube: e.target.value}}})}
                                placeholder="Lien YouTube"
                                className="w-full bg-black/40 border border-white/10 rounded-xl pl-10 pr-4 py-2 text-xs text-white focus:outline-none focus:border-red-600/50"
                              />
                            </div>
                            <div className="relative">
                              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#ff5a00]" />
                              <input 
                                type="text"
                                value={editedFest.branding?.socials?.website || ''}
                                onChange={e => setEditedFest({...editedFest, branding: {...editedFest.branding!, socials: {...editedFest.branding?.socials, website: e.target.value}}})}
                                placeholder="Site Officiel"
                                className="w-full bg-black/40 border border-white/10 rounded-xl pl-10 pr-4 py-2 text-xs text-white focus:outline-none focus:border-[#ff5a00]/50"
                              />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-3">
                          {fest.branding?.socials?.instagram && <Instagram className="w-5 h-5 text-pink-500 opacity-60" />}
                          {fest.branding?.socials?.facebook && <Facebook className="w-5 h-5 text-blue-600 opacity-60" />}
                          {fest.branding?.socials?.tiktok && <Music className="w-5 h-5 text-white opacity-60" />}
                          {fest.branding?.socials?.linkedin && <Linkedin className="w-5 h-5 text-blue-500 opacity-60" />}
                          {fest.branding?.socials?.youtube && <Youtube className="w-5 h-5 text-red-600 opacity-60" />}
                          {fest.branding?.socials?.website && <Globe className="w-5 h-5 text-[#ff5a00] opacity-60" />}
                          {!fest.branding?.socials && <span className="text-gray-500 italic text-sm">Non renseignés</span>}
                        </div>
                      )}
                    </div>
                 
                 <div>
                   <div className="flex items-center justify-between mb-4">
                     <h3 className="text-xl font-bold flex items-center"><ImageIcon className="w-5 h-5 mr-2 text-[#FF5C00]" /> Le Pitch & Présentation</h3>
                     {!isEditingDescription && (
                       <button 
                         onClick={() => {
                           setEditDescriptionText(fest.branding?.description || '');
                           setIsEditingDescription(true);
                         }}
                         className="text-xs font-bold text-[#FF5C00] hover:text-white bg-[#FF5C00]/10 hover:bg-[#FF5C00]/30 border border-[#FF5C00]/20 px-3 py-1.5 rounded-lg transition-colors flex items-center"
                       >
                         <Edit3 className="w-3 h-3 mr-1" /> Modifier
                       </button>
                     )}
                   </div>
                   <div className="p-6 bg-[#1A1A1A] border border-white/5 rounded-2xl relative">
                      {fest.branding?.highlights && fest.branding.highlights.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {fest.branding.highlights.map((highlight: string, i: number) => (
                            <span key={i} className="px-2.5 py-1 bg-[#4169E1]/10 text-[#4169E1] border border-[#4169E1]/20 rounded-md text-xs font-bold">
                              {highlight}
                            </span>
                          ))}
                        </div>
                      )}
                      {isEditingDescription ? (
                        <div className="space-y-4">
                          <textarea 
                            value={editDescriptionText}
                            onChange={(e) => setEditDescriptionText(e.target.value)}
                            className="w-full bg-[#121212] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#FF5C00] transition-colors"
                            rows={6}
                            placeholder="Parlez de l'ambiance, du public visé, de votre histoire..."
                          />
                          <div className="flex justify-end space-x-3">
                            <button
                              onClick={() => setIsEditingDescription(false)}
                              className="px-4 py-2 text-sm font-bold text-gray-400 hover:text-white transition-colors"
                            >
                              Annuler
                            </button>
                            <button
                              onClick={handleSaveDescription}
                              disabled={isSavingDescription}
                              className="px-4 py-2 bg-[#FF5C00] hover:bg-[#FF5C00]/80 text-white text-sm font-bold rounded-lg transition-colors disabled:opacity-50"
                            >
                              {isSavingDescription ? 'Sauvegarde...' : 'Sauvegarder'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-300 text-sm whitespace-pre-line leading-relaxed">
                          {fest.branding?.description || <span className="italic text-gray-500">Aucune description renseignée.</span>}
                        </p>
                      )}
                    </div>

                      <div className="md:col-span-2">
                         <label className="block text-xs font-black text-gray-400 mb-3 uppercase tracking-widest">Lien pour la Billetterie (Shotgun, Dice, etc.)</label>
                         {isEditingEvent ? (
                           <input 
                             type="url" 
                             value={editedFest.ticketingUrl || ''} 
                             onChange={e => setEditedFest({...editedFest, ticketingUrl: e.target.value})} 
                             className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#FF5C00]" 
                             placeholder="https://shotgun.live/fr/events/..."
                           />
                         ) : (
                           <div className="flex items-center space-x-2">
                             {fest.ticketingUrl ? (
                               <a 
                                 href={fest.ticketingUrl} 
                                 target="_blank" 
                                 rel="noopener noreferrer" 
                                 className="text-[#FF5C00] font-bold text-sm hover:underline flex items-center"
                               >
                                 <Globe className="w-4 h-4 mr-1" /> Voir la billetterie
                               </a>
                             ) : (
                               <span className="text-gray-500 italic text-sm">Non renseigné.</span>
                             )}
                           </div>
                         )}
                      </div>

                    <div className="mt-8 space-y-8">
                        <div>
                         <h3 className="text-xl font-bold flex items-center mb-4">
                           <Users className="w-5 h-5 mr-2 text-pink-500" /> Profil du Public (Vibe Check)
                         </h3>
                         <div className="p-6 bg-[#1A1A1A] border border-white/5 rounded-2xl">
                           {isEditingEvent ? (
                             <textarea 
                               value={editedFest.audienceProfile || ''}
                               onChange={e => setEditedFest({...editedFest, audienceProfile: e.target.value})}
                               className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#FF5C00]"
                               rows={3}
                               placeholder="Ex: 18-35 ans, urbains, fêtards, techno..."
                             />
                           ) : (
                             <p className="text-gray-300 text-sm whitespace-pre-line leading-relaxed">
                               {fest.audienceProfile || <span className="italic text-gray-500">Non renseigné.</span>}
                             </p>
                           )}
                         </div>
                       </div>

                       <div className="border-t border-white/5 pt-8">
                          <h3 className="text-xl font-bold flex items-center mb-6 text-[#ff5a00]">
                            <Star className="w-5 h-5 mr-2" /> Historique de l'événement
                          </h3>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                             <div className="bg-[#1A1A1A] p-6 rounded-2xl border border-white/5">
                                <label className="block text-xs font-black text-gray-400 mb-3 uppercase tracking-widest text-[#ff5a00]">Numéro d'édition</label>
                                {isEditingEvent ? (
                                  <input 
                                    type="number" 
                                    value={editedFest.editionNumber || ''}
                                    onChange={e => setEditedFest({...editedFest, editionNumber: e.target.value})}
                                    className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#ff5a00]"
                                    placeholder="Ex: 5"
                                  />
                                ) : (
                                  <p className="text-xl font-black text-white">{fest.editionNumber ? `${fest.editionNumber}ème édition` : 'N/A'}</p>
                                )}
                             </div>

                             <div className="bg-[#1A1A1A] p-6 rounded-2xl border border-white/5">
                                <label className="block text-xs font-black text-gray-400 mb-3 uppercase tracking-widest">Affluence Passée (Total)</label>
                                {isEditingEvent ? (
                                  <input 
                                    type="text" 
                                    value={editedFest.pastAudience || ''}
                                    onChange={e => setEditedFest({...editedFest, pastAudience: e.target.value})}
                                    className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#FF5C00]"
                                    placeholder="Ex: 12k personnes"
                                  />
                                ) : (
                                  <p className="text-xl font-black text-white">{fest.pastAudience || 'N/A'}</p>
                                )}
                             </div>

                             <div className="md:col-span-2 bg-[#1A1A1A] p-6 rounded-2xl border border-white/5">
                                <label className="block text-xs font-black text-gray-400 mb-3 uppercase tracking-widest">Description de l'historique</label>
                                {isEditingEvent ? (
                                  <textarea 
                                    value={editedFest.historyDescription || ''}
                                    onChange={e => setEditedFest({...editedFest, historyDescription: e.target.value})}
                                    className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#FF5C00]"
                                    rows={3}
                                    placeholder="Highlights des années précédentes..."
                                  />
                                ) : (
                                  <p className="text-gray-300 text-sm italic">{fest.historyDescription || 'Aucun historique renseigné.'}</p>
                                )}
                             </div>
                          </div>
                       </div>

                       <div>
                         <h3 className="text-xl font-bold flex items-center mb-4">
                           <Info className="w-5 h-5 mr-2 text-indigo-500" /> À propos (Dossier complet)
                         </h3>
                         <div className="p-6 bg-[#1A1A1A] border border-white/5 rounded-2xl">
                           {isEditingEvent ? (
                             <textarea 
                               value={editedFest.about || ''}
                               onChange={e => setEditedFest({...editedFest, about: e.target.value})}
                               className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#FF5C00]"
                               rows={4}
                               placeholder="L'histoire du festival, les valeurs, les détails pour les stands..."
                             />
                           ) : (
                             <p className="text-gray-300 text-sm whitespace-pre-line leading-relaxed">
                               {fest.about || <span className="italic text-gray-500">Non renseigné.</span>}
                             </p>
                           )}
                         </div>
                       </div>

                       <div>
                         <h3 className="text-xl font-bold flex items-center mb-4">
                           <MapIcon className="w-5 h-5 mr-2 text-blue-500" /> Comment y accéder ?
                         </h3>
                         <div className="p-6 bg-[#1A1A1A] border border-white/5 rounded-2xl">
                           {isEditingEvent ? (
                             <textarea 
                               value={editedFest.accessInfo || ''}
                               onChange={e => setEditedFest({...editedFest, accessInfo: e.target.value})}
                               className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#FF5C00]"
                               rows={3}
                               placeholder="Accès livraison, parkings exposants, contraintes horaires..."
                             />
                           ) : (
                             <p className="text-gray-300 text-sm whitespace-pre-line leading-relaxed">
                               {fest.accessInfo || <span className="italic text-gray-500">Non renseigné.</span>}
                             </p>
                           )}
                         </div>
                       </div>
                    </div>
                  </div>

                  {fest.branding?.gallery && fest.branding.gallery.length > 0 && (
                    <div>
                      <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">Galerie Photos ({fest.branding.gallery.length})</h3>
                      <div className="flex gap-3 overflow-x-auto pb-4">
                        {fest.branding.gallery.map((img: string, idx: number) => (
                          <img key={idx} src={img} alt={`Gallery ${idx}`} className="h-24 w-24 object-cover rounded-xl border border-white/10 shrink-0" />
                        ))}
                      </div>
                    </div>
                  )}
               </div>

               {/* Right Col: Technical & Pitches */}
               <div className="space-y-6">
                  
                  <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-6">
                    <h3 className="text-lg font-bold mb-5 flex items-center text-white"><Truck className="w-5 h-5 mr-2 text-indigo-500" /> Logistique</h3>
                    <ul className="space-y-4">
                      <li className="flex items-start">
                        <Zap className="w-4 h-4 text-yellow-500 mr-3 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-bold text-white">Énergie</p>
                          <p className="text-xs text-gray-400">{fest.technical?.powerKva || 0} kVA total</p>
                          <div className="flex space-x-2 mt-1">
                            {fest.technical?.has16A && <span className="text-[10px] bg-white/10 px-1.5 py-0.5 rounded">16A</span>}
                            {fest.technical?.has32A && <span className="text-[10px] bg-white/10 px-1.5 py-0.5 rounded">32A</span>}
                            {fest.technical?.has63A && <span className="text-[10px] bg-white/10 px-1.5 py-0.5 rounded">63A</span>}
                          </div>
                        </div>
                      </li>
                      <li className="flex items-start">
                        <Droplet className="w-4 h-4 text-blue-500 mr-3 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-bold text-white">Eau & Fluides</p>
                          <p className="text-xs text-gray-400">
                            Potable: {fest.technical?.hasDrinkingWater ? 'Oui' : 'Non'} <br/>
                            Grises: {fest.technical?.wastewaterDetails || 'Non spécifié'}
                          </p>
                        </div>
                      </li>
                      <li className="flex items-start">
                        <ShieldCheck className="w-4 h-4 text-green-500 mr-3 shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-bold text-white">Sécurité la nuit</p>
                          {isEditingEvent ? (
                            <textarea 
                              value={editedFest.technical?.nightSecurity || ''}
                              onChange={e => setEditedFest({
                                ...editedFest, 
                                technical: { ...editedFest.technical, nightSecurity: e.target.value } as any
                              })}
                              className="mt-1 w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-[#FF5C00]"
                              rows={2}
                              placeholder="Gardiennage, maîtres-chiens..."
                            />
                          ) : (
                            <p className="text-xs text-gray-400">
                              {fest.technical?.nightSecurity || 'Non spécifié'}
                            </p>
                          )}
                        </div>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-6">
                    <h3 className="text-lg font-bold mb-4 flex items-center text-white"><Ruler className="w-5 h-5 mr-2 text-green-500" /> Gestion des Places ({fest.totalStands || 0} total)</h3>
                    <div className="space-y-3">
                      {fest.technical?.pitches && fest.technical.pitches.length > 0 ? (
                        fest.technical.pitches.map((pitch: PitchFormat, idx: number) => (
                          <div key={idx} className="bg-white/5 border border-white/10 rounded-xl p-3 flex justify-between items-center">
                            <div>
                              <div className="font-bold text-sm text-white">Format {idx + 1}</div>
                              <div className="text-xs text-gray-400 capitalize">{pitch.width}x{pitch.depth}m • {pitch.flooring}</div>
                            </div>
                            <div className="text-[#FF5C00] font-black">{pitch.price}€</div>
                          </div>
                        ))
                      ) : (
                        <p className="text-xs text-gray-500 italic">Aucun format configuré</p>
                      )}
                      
                      <div className="pt-3 mt-3 border-t border-white/10">
                        <p className="text-xs text-gray-400">Tables fournies : <span className="text-white font-bold">{fest.technical?.providesTables ? 'Oui' : 'Non'}</span></p>
                      </div>
                    </div>
                  </div>

               </div>
            </div>
          </div>
        </div>
        )}

        {activeTab === 'applications' && (
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold">Candidatures reçues</h2>
                <div className="bg-[#FF5C00]/20 text-[#FF5C00] px-3 py-1 rounded-full text-xs font-black border border-[#FF5C00]/30">
                  {applications.length}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
                <div className="relative w-full sm:w-64">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Rechercher un stand..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="block w-full pl-10 pr-3 py-2 bg-[#1A1A1A] border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#FF5C00] focus:border-transparent text-sm"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="bg-[#1A1A1A] border border-white/10 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5C00] w-full sm:w-auto"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="pending">En attente</option>
                  <option value="approved">Accepté</option>
                  <option value="rejected">Refusé</option>
                </select>
                <button
                  onClick={() => setSortByScore(v => !v)}
                  className={`px-4 py-2 rounded-lg text-sm font-bold border transition-all whitespace-nowrap ${sortByScore ? 'bg-[#FF5C00]/20 text-[#FF5C00] border-[#FF5C00]/40' : 'bg-[#1A1A1A] text-gray-400 border-white/10 hover:text-white'}`}
                >
                  {sortByScore ? '★ Score ↓' : '☆ Trier par score'}
                </button>
              </div>
            </div>

            <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-white/5 text-xs uppercase tracking-wider text-gray-500">
                      <th className="px-6 py-4 font-bold">Stand</th>
                      <th className="px-6 py-4 font-bold">Catégorie</th>
                      <th className="px-6 py-4 font-bold">Score</th>
                      <th className="px-6 py-4 font-bold">Format</th>
                      <th className="px-6 py-4 font-bold">Date de candidature</th>
                      <th className="px-6 py-4 font-bold">Statut</th>
                      <th className="px-6 py-4 font-bold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {currentApplications.length > 0 ? (
                      currentApplications.map(app => (
                        <React.Fragment key={app.id}>
                        <tr className="hover:bg-white/5 transition-colors group cursor-pointer" onClick={() => setExpandedAppId(prev => prev === app.id ? null : app.id)}>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-3">
                              <div className="font-bold text-white tracking-tight">{app.standName}</div>
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(`/stand-detail/${app.standId}`, '_blank');
                                }}
                                className="p-1.5 bg-white/5 hover:bg-[#FF5C00]/20 text-gray-500 hover:text-[#FF5C00] rounded-lg transition-all border border-white/5 hover:border-[#FF5C00]/30 group/eye"
                                title="Voir le profil du stand (nouvel onglet)"
                              >
                                <Eye className="w-3.5 h-3.5 group-hover/eye:scale-110 transition-transform" />
                              </button>
                            </div>
                            <div className="text-xs text-gray-500 font-medium">{app.applicantName}</div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="bg-[#4169E1]/10 text-[#4169E1] px-2.5 py-1 rounded-md text-xs font-bold border border-[#4169E1]/20">
                              {standTypeLabels[app.category] || app.category}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            {(() => {
                              const stand = standsData[app.standId];
                              if (!stand) return <span className="text-gray-600 text-xs">—</span>;
                              const s = computeMatchScore(stand, fest || {});
                              return (
                                <div className="group relative">
                                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-black border ${s.bgColor} ${s.color}`}>
                                    {s.total}/100
                                  </span>
                                  {/* Tooltip breakdown */}
                                  <div className="absolute left-0 top-8 z-50 hidden group-hover:block w-44 bg-[#0d0d0d] border border-white/10 rounded-xl p-3 shadow-2xl text-xs space-y-1.5">
                                    <p className={`font-black text-sm mb-2 ${s.color}`}>{s.label}</p>
                                    <div className="flex justify-between"><span className="text-gray-400">Catégorie</span><span className="font-bold text-white">{s.breakdown.category}/30</span></div>
                                    <div className="flex justify-between"><span className="text-gray-400">Documents</span><span className="font-bold text-white">{s.breakdown.documents}/20</span></div>
                                    <div className="flex justify-between"><span className="text-gray-400">Expérience</span><span className="font-bold text-white">{s.breakdown.experience}/20</span></div>
                                    <div className="flex justify-between"><span className="text-gray-400">Médias</span><span className="font-bold text-white">{s.breakdown.media}/20</span></div>
                                    <div className="flex justify-between"><span className="text-gray-400">Région</span><span className="font-bold text-white">{s.breakdown.region}/10</span></div>
                                  </div>
                                </div>
                              );
                            })()}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-300">
                            {app.format}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-300">
                            {new Date(app.appliedAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </td>
                          <td className="px-6 py-4">
                            {app.status === 'pending' && (
                              <span className="inline-flex items-center gap-1.5 bg-yellow-500/10 text-yellow-500 px-2.5 py-1 rounded-lg text-xs font-bold border border-yellow-500/20">
                                <span className="w-1.5 h-1.5 rounded-full bg-yellow-500"></span> En attente
                              </span>
                            )}
                            {app.status === 'approuve_paiement_requis' && (
                              <span className="inline-flex items-center gap-1.5 bg-blue-500/10 text-blue-400 px-2.5 py-1 rounded-lg text-xs font-bold border border-blue-500/20">
                                <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse"></span> Validé · en attente du stand
                              </span>
                            )}
                            {(app.status === 'approved' || app.status === 'confirme') && (
                              <span className="inline-flex items-center gap-1.5 bg-green-500/10 text-green-500 px-2.5 py-1 rounded-lg text-xs font-bold border border-green-500/20">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span> {app.status === 'confirme' ? 'Confirmé' : 'Accepté'}
                              </span>
                            )}
                            {app.status === 'rejected' && (
                              <span className="inline-flex items-center gap-1.5 bg-red-500/10 text-red-500 px-2.5 py-1 rounded-lg text-xs font-bold border border-red-500/20">
                                <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span> Refusé
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-right space-x-2">
                            {app.status === 'pending' && (
                              <>
                                <button 
                                  onClick={(e) => { e.stopPropagation(); handleUpdateStatus(app.id, 'approuve_paiement_requis'); }}
                                  className="text-[#FF5C00] hover:bg-[#FF5C00]/10 h-10 md:h-9 px-4 rounded-xl transition-all text-xs font-bold border border-[#FF5C00]/20 active:scale-95 whitespace-nowrap"
                                >
                                  Valider le stand
                                </button>
                                <button 
                                  onClick={(e) => { e.stopPropagation(); handleUpdateStatus(app.id, 'rejected'); }}
                                  className="text-gray-500 hover:bg-white/5 h-10 md:h-9 px-3 transition-colors text-xs font-bold active:scale-95"
                                >
                                  × Refuser
                                </button>
                              </>
                            )}
                            {app.status === 'approuve_paiement_requis' && (
                              <span className="text-[10px] text-gray-500 font-bold uppercase italic">En attente de paiement...</span>
                            )}
                            <button 
                              onClick={(e) => { e.stopPropagation(); setExpandedAppId(prev => prev === app.id ? null : app.id); }}
                              className="text-gray-400 hover:text-white font-bold text-xs uppercase tracking-widest transition-colors h-10 md:h-9 px-4 active:scale-95"
                            >
                              {expandedAppId === app.id ? 'Fermer' : 'Voir'}
                            </button>
                          </td>
                        </tr>
                        {expandedAppId === app.id && (
                          <tr key={`${app.id}-details`} className="bg-white/5">
                            <td colSpan={6} className="px-6 py-6 border-b border-white/5">
                              <div className="flex flex-col md:flex-row gap-6">
                                <div className="flex-1 space-y-4">
                                  <h4 className="text-lg font-bold text-white mb-2">Détails de la candidature</h4>
                                  <div className="flex items-center space-x-3 mb-4">
                                    <button 
                                      onClick={() => navigate(`/stand-detail/${app.standId}`)}
                                      className="flex items-center space-x-2 bg-white/5 hover:bg-white/10 text-white h-12 md:h-11 px-6 rounded-xl border border-white/10 transition-all text-xs font-bold group active:scale-[0.98]"
                                    >
                                      <Eye className="w-4 h-4 text-[#FF5C00]" />
                                      <span>Voir le profil (nouvel onglet)</span>
                                      <ArrowRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                                    </button>
                                  </div>
                                  <p className="text-sm text-gray-300">Responsable : <span className="font-semibold text-white">{app.applicantName}</span></p>
                                  <p className="text-sm text-gray-300">Format : <span className="font-semibold text-white">{app.format}</span></p>
                                  <p className="text-sm text-gray-300">Catégorie : <span className="font-semibold text-white">{app.category}</span></p>

                                  {/* Message de motivation du candidat */}
                                  {app.messageMotivation && (
                                    <div className="mt-4 p-3 bg-[#FF5C00]/5 border border-[#FF5C00]/20 rounded-xl">
                                      <p className="text-[10px] font-black uppercase tracking-widest text-[#FF5C00] flex items-center gap-1.5 mb-1.5">
                                        <MessageSquare className="w-3 h-3" /> Message du candidat
                                      </p>
                                      <p className="text-sm text-gray-200 leading-relaxed whitespace-pre-wrap italic">« {app.messageMotivation} »</p>
                                    </div>
                                  )}

                                  {/* Coordonnées débloquées après paiement */}
                                  {app.status === 'confirme' && standsData[app.standId] ? (
                                    <div className="mt-4 p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-xl space-y-2">
                                      <p className="text-[10px] font-black uppercase tracking-widest text-emerald-400 flex items-center gap-1.5">
                                        <CheckCircle className="w-3 h-3" /> Coordonnées débloquées
                                      </p>
                                      {standsData[app.standId].email && (
                                        <a href={`mailto:${standsData[app.standId].email}`}
                                          className="flex items-center gap-2 text-sm text-gray-300 hover:text-white transition-colors">
                                          <Mail className="w-4 h-4 text-gray-500 shrink-0" />
                                          {standsData[app.standId].email}
                                        </a>
                                      )}
                                      {standsData[app.standId].phone && (
                                        <a href={`tel:${standsData[app.standId].phone}`}
                                          className="flex items-center gap-2 text-sm text-gray-300 hover:text-white transition-colors">
                                          <Phone className="w-4 h-4 text-gray-500 shrink-0" />
                                          {standsData[app.standId].phone}
                                        </a>
                                      )}
                                      {!standsData[app.standId].email && !standsData[app.standId].phone && (
                                        <p className="text-xs text-gray-500 italic">L'exposant n'a pas renseigné ses coordonnées</p>
                                      )}
                                    </div>
                                  ) : app.status !== 'confirme' ? (
                                    <div className="mt-4 flex items-center gap-2 text-xs text-gray-500 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5">
                                      <span>🔒</span>
                                      <span>Coordonnées visibles après paiement de l'exposant</span>
                                    </div>
                                  ) : null}
                                </div>
                                <div className="flex-1 space-y-4">
                                  <h4 className="text-lg font-bold text-white mb-2">Contrat de location</h4>
                                  {app.status === 'approved' ? (
                                    <div className="bg-black/30 p-4 rounded-xl border border-white/10">
                                      <p className="text-sm text-gray-400 mb-3">Générez et envoyez le contrat de location incluant le tarif et le format.</p>
                                      
                                      {!contractStates[app.id] || contractStates[app.id].status === 'idle' ? (
                                        <button 
                                          onClick={() => handleGenerateContract(app)}
                                          className="w-full bg-white text-black font-black h-12 md:h-11 rounded-xl hover:bg-gray-200 transition-colors flex items-center justify-center text-xs uppercase tracking-widest active:scale-[0.98]"
                                        >
                                          📄 Générer le contrat
                                        </button>
                                      ) : contractStates[app.id].status === 'generating' ? (
                                        <div className="w-full bg-[#1A1A1A] text-gray-400 font-bold py-2 rounded-lg flex items-center justify-center text-sm border border-white/10">
                                          ⏳ En attente de génération...
                                        </div>
                                      ) : contractStates[app.id].status === 'ready' ? (
                                        <div className="space-y-3">
                                          <div className="w-full bg-green-500/10 text-green-500 font-bold py-2 rounded-lg flex items-center justify-center text-sm border border-green-500/20">
                                            ✅ Prêt à envoyer.
                                          </div>
                                          <div className="flex gap-2">
                                            <a 
                                              href={contractStates[app.id].url} 
                                              target="_blank" 
                                              rel="noreferrer"
                                              className="flex-1 flex justify-center items-center bg-black border border-white/20 hover:border-white/40 text-white font-bold py-2 rounded-lg transition-colors text-sm"
                                            >
                                              Aperçu PDF
                                            </a>
                                            <button 
                                              onClick={() => alert(`Contrat envoyé par email à ${app.applicantName}.`)}
                                              className="flex-1 bg-[#FF5C00] hover:bg-[#FF5C00]/80 text-white font-bold py-2 rounded-lg transition-colors text-sm"
                                            >
                                              Envoyer par mail
                                            </button>
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="space-y-3">
                                          <div className="w-full bg-red-500/10 text-red-500 font-bold py-2 px-3 rounded-lg text-xs border border-red-500/20">
                                            ❌ Erreur: {contractStates[app.id].error}
                                          </div>
                                          <button 
                                            onClick={() => handleGenerateContract(app)}
                                            className="w-full bg-white text-black font-bold py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center text-sm"
                                          >
                                            🔄 Réessayer
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="bg-black/30 p-4 rounded-xl border border-white/10 flex items-center justify-center h-[100px]">
                                      <p className="text-sm text-gray-500 italic">Acceptez la candidature d'abord</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                          Aucune candidature trouvée.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-white/5 flex items-center justify-between">
                  <div className="text-sm text-gray-400">
                    Affichage de <span className="text-white font-medium">{indexOfFirstItem + 1}</span> à <span className="text-white font-medium">{Math.min(indexOfLastItem, filteredApplications.length)}</span> sur <span className="text-white font-medium">{filteredApplications.length}</span> résultats
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 bg-[#1A1A1A] border border-white/10 rounded-lg text-sm text-gray-300 hover:bg-white/5 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Précédent
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === page
                            ? 'bg-[#FF5C00] text-white border border-[#FF5C00]'
                            : 'bg-[#1A1A1A] border border-white/10 text-gray-300 hover:bg-white/5 hover:text-white'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 bg-[#1A1A1A] border border-white/10 rounded-lg text-sm text-gray-300 hover:bg-white/5 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Suivant
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'plan' && (
          <div className="space-y-6 relative">
            {/* Locking Overlay for V2 Teasing */}
            <div className="absolute inset-0 z-50 backdrop-blur-md bg-black/60 flex items-center justify-center rounded-3xl p-6">
              <div className="bg-zinc-950 border border-zinc-800 p-8 rounded-3xl max-w-lg w-full text-center shadow-2xl">
                <div className="inline-block px-4 py-1.5 rounded-full bg-[#FF5E00]/10 border border-[#FF5E00]/20 text-[#FF5E00] text-xs font-black uppercase tracking-widest mb-6">
                  🚧 Bientôt disponible pour la V2
                </div>
                <h3 className="text-xl font-black text-white uppercase tracking-tight mb-4">Le plan interactif et l'auto-placement arrivent très vite. Découvrez ce qui vous attend :</h3>
                <div className="space-y-4 text-left mb-8">
                  <p className="text-sm text-gray-300 flex gap-2"><span className="text-[#FF5E00]">📍</span> Placement 2D/3D Interactif : Dessinez le plan de votre événement et glissez-déposez vos stands en quelques clics.</p>
                  <p className="text-sm text-gray-300 flex gap-2"><span className="text-[#FF5E00]">⚡</span> Auto-placement intelligent : Laissez notre algorithme placer automatiquement les stands selon leurs besoins techniques.</p>
                  <p className="text-sm text-gray-300 flex gap-2"><span className="text-[#FF5E00]">🔌</span> Gestion des flux techniques : Visualisez instantanément les accès aux points d'eau, bornes électriques et issues de secours.</p>
                  <p className="text-sm text-gray-300 flex gap-2"><span className="text-[#FF5E00]">📥</span> Export de plan pro : Téléchargez le plan technique complet au format PDF/CSV pour vos équipes logistiques sur le terrain.</p>
                </div>
                <button className="bg-[#FF5E00] text-white font-black h-12 w-full rounded-xl hover:bg-[#FF5E00]/80 transition-colors uppercase tracking-widest text-xs">Être notifié à la sortie</button>
              </div>
            </div>
            
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <h2 className="text-2xl font-bold">Plan & Technique</h2>
              <div className="flex bg-[#1A1A1A] rounded-xl p-1 border border-white/10">
                <button
                  onClick={() => setToolMode('select')}
                  className={`p-3 rounded-lg flex items-center transition-all ${toolMode === 'select' ? 'bg-[#FF5C00] text-white shadow-lg' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
                  title="Sélectionner"
                >
                  <MousePointer2 className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setToolMode('zone')}
                  className={`p-3 rounded-lg flex items-center transition-all ${toolMode === 'zone' ? 'bg-[#FF5C00] text-white shadow-lg' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
                  title="Tracer une zone"
                >
                  <Square className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setToolMode('pin_elec')}
                  className={`p-3 rounded-lg flex items-center transition-all ${toolMode === 'pin_elec' ? 'bg-[#FF5C00] text-white shadow-lg' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
                  title="Ajouter point électricité"
                >
                  <Zap className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setToolMode('pin_eau')}
                  className={`p-3 rounded-lg flex items-center transition-all ${toolMode === 'pin_eau' ? 'bg-[#FF5C00] text-white shadow-lg' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
                  title="Ajouter point d'eau"
                >
                  <Droplet className="w-5 h-5" />
                </button>
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={handleExportLogistics}
                  disabled={isExporting}
                  className="bg-[#1A1A1A] border border-white/10 hover:bg-white/5 text-white px-4 py-3 rounded-xl font-bold transition-all flex items-center"
                  title="Exporter la logistique (CSV)"
                >
                  <Download className={`w-5 h-5 ${isExporting ? 'animate-bounce text-gray-500' : 'text-blue-500'}`} />
                  <span className="ml-2 hidden lg:inline">{isExporting ? 'Export...' : 'Exporter CSV'}</span>
                </button>
                <button 
                  onClick={handleOptimizePlan}
                  className="bg-[#1A1A1A] border border-white/10 hover:bg-white/5 text-white px-6 py-3 rounded-xl font-bold transition-all flex items-center"
                >
                  <Zap className="w-5 h-5 mr-2 text-[#FF5C00]" />
                  Auto-placement stands
                </button>
              </div>
            </div>

            <div className="flex gap-6 relative">
              <div className="flex-1 bg-[#121212] border border-white/10 rounded-3xl p-6 overflow-x-auto overflow-y-hidden">
                 <div className="w-[800px] h-[600px] border-2 border-white/10 rounded-2xl relative bg-black shrink-0 cursor-crosshair overflow-hidden" 
                      style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '20px 20px', backgroundPosition: 'center center' }}
                      onMouseDown={handleMapMouseDown}
                      onMouseMove={handleMapMouseMove}
                      onMouseUp={handleMapMouseUp}
                      onMouseLeave={handleMapMouseUp}
                  >
                    
                    {planElements.map(element => {
                      const isSelected = selectedElementId === element.id;
                      if (element.type === 'stand' || element.type === 'zone') {
                        return (
                          <div 
                            key={element.id}
                            onMouseDown={(e) => {
                              if (toolMode === 'select') {
                                e.stopPropagation();
                                setSelectedElementId(element.id);
                                setIsDraggingElement(true);
                                setDragOffset({
                                  x: e.clientX - element.x,
                                  y: e.clientY - element.y
                                });
                              }
                            }}
                            className={`absolute flex items-center justify-center p-1 text-center shadow-lg cursor-pointer
                               ${element.type === 'stand' ? 'bg-[#FF5C00]/80 border-[#FF5C00]' : 'bg-blue-500/30 border-blue-500'}
                               ${isSelected ? 'border-4 ring-2 ring-white/50' : 'border-2'}`}
                            style={{ 
                              left: element.x, 
                              top: element.y, 
                              width: element.w, 
                              height: element.h,
                              transition: isDrawing ? 'none' : 'all 0.3s ease-out',
                              borderRadius: element.type === 'zone' ? '12px' : '4px'
                            }}
                          >
                            <span className="text-[10px] font-bold text-white leading-tight break-words overflow-hidden w-full h-full flex flex-col items-center justify-center">
                              {element.name}
                              {element.type === 'zone' && element.price !== undefined && (
                                <span className="text-[8px] bg-black/50 px-1 rounded mt-1">{element.price}€</span>
                              )}
                            </span>
                          </div>
                        );
                      } else if (element.type === 'pin_elec' || element.type === 'pin_eau') {
                        return (
                          <div
                            key={element.id}
                            onMouseDown={(e) => {
                              if (toolMode === 'select') {
                                e.stopPropagation();
                                setSelectedElementId(element.id);
                                setIsDraggingElement(true);
                                setDragOffset({
                                  x: e.clientX - element.x,
                                  y: e.clientY - element.y
                                });
                              }
                            }}
                            className={`absolute flex items-center justify-center rounded-full shadow-lg cursor-pointer
                               ${element.type === 'pin_elec' ? 'bg-yellow-500 text-black' : 'bg-blue-400 text-white'}
                               ${isSelected ? 'ring-4 ring-white shadow-[0_0_15px_rgba(255,255,255,0.5)]' : ''}`}
                            style={{
                              left: element.x,
                              top: element.y,
                              width: element.w,
                              height: element.h,
                            }}
                          >
                            {element.type === 'pin_elec' ? <Zap className="w-4 h-4" /> : <Droplet className="w-4 h-4" />}
                          </div>
                        );
                      }
                      return null;
                    })}
                    
                    {isDrawing && toolMode === 'zone' && (
                      <div 
                        className="absolute border-2 border-blue-500/50 bg-blue-500/20"
                        style={{
                          left: currentDraw.x,
                          top: currentDraw.y,
                          width: currentDraw.w,
                          height: currentDraw.h,
                          borderRadius: '12px'
                        }}
                      />
                    )}
                 </div>
              </div>

              {/* Sidebar Edit Panel */}
              <div className={`w-64 bg-[#1A1A1A] border border-white/10 rounded-3xl p-6 transition-all duration-300 flex flex-col ${selectedElementId ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4 pointer-events-none'}`}>
                <h3 className="text-lg font-bold mb-6 flex justify-between items-center">
                  Édition
                  <button onClick={handleDeleteElement} className="text-red-500 hover:bg-red-500/10 p-2 rounded-lg transition-colors">
                    <Trash2 className="w-5 h-5"/>
                  </button>
                </h3>

                {selectedElementId && (() => {
                  const el = planElements.find(e => e.id === selectedElementId);
                  if (!el) return null;
                  
                  return (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Type</label>
                        <div className="font-medium">
                          {el.type === 'stand' ? 'Stand Placé' : el.type === 'zone' ? 'Zone Réservable' : el.type === 'pin_elec' ? 'Électricité' : 'Point d\'Eau'}
                        </div>
                      </div>
                      
                      {(el.type === 'stand' || el.type === 'zone') && (
                        <div>
                          <label className="block text-sm text-gray-400 mb-1">Nom</label>
                          <input 
                            type="text"
                            value={el.name || ''}
                            onChange={(e) => {
                              const newName = e.target.value;
                              const newElements = planElements.map(x => x.id === el.id ? { ...x, name: newName } : x);
                              setPlanElements(newElements);
                              savePlanElements(newElements);
                            }}
                            className="w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF5C00]"
                          />
                        </div>
                      )}

                      {el.type === 'zone' && (
                        <div>
                          <label className="block text-sm text-gray-400 mb-1">Tarif (€)</label>
                          <input 
                            type="number"
                            value={el.price || 0}
                            onChange={(e) => handleUpdatePrice(e.target.value)}
                            className="w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF5C00]"
                          />
                        </div>
                      )}
                    </div>
                  );
                })()}

              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="max-w-3xl space-y-8">
            <h2 className="text-3xl font-black tracking-tight mb-8">Paramètres</h2>

            {/* Documents */}
            <div className="bg-[#1A1A1A] border border-white/10 rounded-3xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <ShieldCheck className="w-6 h-6 text-emerald-400" />
                <div>
                  <h3 className="text-lg font-bold text-white">Assurance RC Pro</h3>
                  <p className="text-sm text-gray-500">Optionnel — affiche un badge "Assuré ✓" sur votre fiche festival pour rassurer les exposants</p>
                </div>
                {fest.insuranceUrl && (
                  <span className="ml-auto px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-xs font-bold text-emerald-400 uppercase tracking-wider shrink-0">
                    Reçu ✓
                  </span>
                )}
              </div>

              <DocumentDropzone
                label="Attestation d'assurance RC Pro"
                storagePath={`festivals/${id}/doc_assurance`}
                existingUrl={fest.insuranceUrl}
                description="PDF ou image — visible uniquement par l'équipe Festin"
                onUploadComplete={async (url) => {
                  if (!id) return;
                  await updateDoc(doc(db, 'festivals', id), { insuranceUrl: url });
                  setFest(prev => prev ? { ...prev, insuranceUrl: url } : prev);
                }}
              />

              {fest.insuranceUrl && (
                <button
                  onClick={async () => {
                    if (!id) return;
                    try {
                      await deleteObject(storageRef(storage, `festivals/${id}/doc_assurance`));
                    } catch {}
                    const { deleteField } = await import('firebase/firestore');
                    await updateDoc(doc(db, 'festivals', id), { insuranceUrl: deleteField() });
                    setFest(prev => prev ? { ...prev, insuranceUrl: undefined } : prev);
                  }}
                  className="mt-3 flex items-center gap-2 text-xs font-bold text-red-400 hover:text-white transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Supprimer le document
                </button>
              )}
            </div>

            <div className="bg-[#1A1A1A] border border-red-500/20 rounded-3xl p-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-red-500/5 blur-[100px] pointer-events-none"></div>
              
              <h3 className="text-xl font-bold text-red-500 mb-2">Zone de danger</h3>
              <p className="text-gray-400 mb-6">
                La suppression de ce festival est définitive. Toutes les données associées (candidatures, plans, vitrine) seront perdues.
              </p>
              
              <div className="bg-black/50 border border-white/5 rounded-2xl p-6">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Pour confirmer, tapez <strong>{fest.name}</strong> ci-dessous :
                </label>
                <input 
                  type="text" 
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  className="w-full bg-[#1A1A1A] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-colors mb-4"
                  placeholder={fest.name}
                />
                
                <button 
                  onClick={handleDeleteFestival}
                  disabled={deleteConfirm !== fest.name || isDeleting}
                  className="w-full bg-red-500/10 hover:bg-red-500/20 text-red-500 font-bold border border-red-500/20 disabled:opacity-50 disabled:cursor-not-allowed hover:border-red-500/50 py-3 rounded-xl transition-all flex items-center justify-center"
                >
                  {isDeleting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-red-500/20 border-t-red-500 rounded-full animate-spin mr-2"></div>
                      Suppression en cours...
                    </>
                  ) : (
                    'Supprimer définitivement le festival'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
